import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

export type Template = 'GS' | 'WFS'
export type Channel = 'normal' | 'ferien'

export interface GroupSchool {
  agencyId: string | null
  schoolName: string
  schoolId: number | null
  template: Template | null
}
export interface Group {
  groupName: string
  template: Template
  mixedTemplate: boolean
  schools: GroupSchool[]
}
export interface Meal {
  mealId: number
  name: string
  category: string | null
  isBio: boolean
  allergens: string
  additives: string
  // Course flags (mig 223) — real, MULTI-SELECT data ("Milchreis kann
  // Hauptgericht UND Dessert sein"). Optional: absent until 223 has run.
  isHauptgericht?: boolean
  isSalat?: boolean
  isDessert?: boolean
  isBeilage?: boolean
}
export interface Slot {
  slotId?: number
  date: string   // YYYY-MM-DD
  kind: string
  mealId: number | null
  position: number
}
export interface BlockedDay { date: string; label: string }
export interface Plan {
  planId: number
  groupName: string
  template: Template
  year: number
  week: number
  status: string
  /** PCA-Übertragungsstatus: live = nach letzter Veröffentlichung von PCA abgeholt. */
  transmission?: 'draft' | 'pending' | 'live'
  lastPublishedAt?: string | null
  lastPolledAt?: string | null
}

export function useGroups() {
  return useQuery({
    queryKey: ['speiseplan-groups'],
    queryFn: () => api.get<{ groups: Group[] }>('/api/speiseplan/groups'),
    staleTime: 10 * 60 * 1000,
  })
}

export function useMeals() {
  return useQuery({
    queryKey: ['speiseplan-meals'],
    queryFn: () => api.get<{ meals: Meal[] }>('/api/speiseplan/meals'),
    staleTime: 10 * 60 * 1000,
  })
}

export function usePlan(group: string | null, year: number, week: number) {
  return useQuery({
    queryKey: ['speiseplan-plan', group, year, week],
    queryFn: () => api.get<{ plan: Plan | null; slots: Slot[]; blockedDays?: BlockedDay[] }>(
      `/api/speiseplan/plan?group=${encodeURIComponent(group!)}&year=${year}&week=${week}`,
    ),
    enabled: !!group && !!year && !!week,
  })
}

export function useSavePlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { groupName: string; template: Template; year: number; week: number; slots: Slot[]; blockedDays: BlockedDay[]; confirmLive?: boolean }) =>
      api.put<{ planId: number; saved: number }>('/api/speiseplan/plan', body),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ['speiseplan-plan', v.groupName, v.year, v.week] }),
  })
}

export function usePublish() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { groupName: string; year: number; week: number }) =>
      api.post<{ published: boolean; channel: Channel; url: string; recipients: number; mailStatus: string }>(
        '/api/speiseplan/publish', body,
      ),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ['speiseplan-plan', v.groupName, v.year, v.week] }),
  })
}

export function useSetChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { groupName: string; channel: Channel }) =>
      api.post<{ groupName: string; activeChannel: Channel }>('/api/speiseplan/channel', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-mailcontacts'] }),
  })
}

// ── Public links (frozen per group — retrievable any time) ───────────────────
export interface ChannelLink { url: string | null; lastPublished: string | null }
export interface GroupLinks { activeChannel: Channel; normal: ChannelLink; ferien: ChannelLink }

export function useGroupLinks(group: string | null) {
  return useQuery({
    queryKey: ['speiseplan-links', group],
    queryFn: () => api.get<GroupLinks>(`/api/speiseplan/links?group=${encodeURIComponent(group!)}`),
    enabled: !!group,
    staleTime: 60 * 1000,
  })
}

// ── Wochen-Übersicht: Veröffentlichungs-/Übertragungsstatus je KW und Gruppe ──
export interface PublicationWeek {
  year: number; week: number; status: string
  lastPublishedAt: string | null
  transmission: 'draft' | 'pending' | 'live'
}

export function usePublications(group: string | null) {
  return useQuery({
    queryKey: ['speiseplan-publications', group],
    queryFn: () => api.get<{ lastPolledAt: string | null; weeks: PublicationWeek[] }>(
      `/api/speiseplan/publications?group=${encodeURIComponent(group!)}`),
    enabled: !!group,
    staleTime: 60 * 1000,
  })
}

// ── PCA-Standardimport (PCA pollt unser JSON-Blob — Link je Gruppe eingefroren) ─
export interface PcaLink { url: string | null; referenceId: string | null; lastGenerated: string | null }

export function usePcaLink(group: string | null) {
  return useQuery({
    queryKey: ['speiseplan-pca-link', group],
    queryFn: () => api.get<PcaLink>(`/api/speiseplan/pca/links?group=${encodeURIComponent(group!)}`),
    enabled: !!group,
    staleTime: 60 * 1000,
  })
}

export interface PcaLinkRow { groupName: string; referenceId: string; url: string | null; lastGenerated: string | null }

export function usePcaLinksList(enabled: boolean) {
  return useQuery({
    queryKey: ['speiseplan-pca-links-list'],
    queryFn: () => api.get<{ links: PcaLinkRow[] }>('/api/speiseplan/pca/links'),
    enabled,
    staleTime: 60 * 1000,
  })
}

export function usePcaPreview(group: string | null) {
  return useQuery({
    queryKey: ['speiseplan-pca-preview', group],
    queryFn: () => api.get<{ url: string; referenceId: string; payload: unknown }>(
      `/api/speiseplan/pca/preview?group=${encodeURIComponent(group!)}`),
    enabled: !!group,
    staleTime: 30 * 1000,
  })
}

export function useRemovePcaChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { groupName: string }) =>
      api.delete<{ removed: boolean; groupName: string }>(
        `/api/speiseplan/pca/channel?group=${encodeURIComponent(body.groupName)}`),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['speiseplan-pca-links-list'] })
      qc.invalidateQueries({ queryKey: ['speiseplan-pca-link', v.groupName] })
      qc.invalidateQueries({ queryKey: ['speiseplan-pca-preview', v.groupName] })
    },
  })
}

export function useRegeneratePca() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { groupName: string }) =>
      api.post<{ results: Array<{ groupName: string; url?: string; referenceId?: string; warnings?: string[]; error?: string }> }>(
        '/api/speiseplan/pca/regenerate', body,
      ),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['speiseplan-pca-link', v.groupName] })
      qc.invalidateQueries({ queryKey: ['speiseplan-pca-links-list'] })
      qc.invalidateQueries({ queryKey: ['speiseplan-pca-preview', v.groupName] })
    },
  })
}

// ── Gerichte management (admin CRUD — Gerichte page) ────────────────────────
export interface AdminMeal extends Meal {
  isActive: boolean
  isHauptgericht: boolean
  isSalat: boolean
  isDessert: boolean
  isBeilage: boolean
  manuallyEdited: boolean
  mensamaxId: string | null
  timesPlanned: number
  productCount: number
  avgRating: number | null
  ratingCount: number
  createdAt: string | null
  createdBy: string | null
  modifiedAt: string | null
  modifiedBy: string | null
}
export interface MealPatch {
  name: string
  category: string
  isBio: boolean
  allergens: string
  additives: string
  isActive: boolean
  isHauptgericht: boolean
  isSalat: boolean
  isDessert: boolean
  isBeilage: boolean
}

export function useAdminMeals() {
  return useQuery({
    queryKey: ['speiseplan-meals-admin'],
    queryFn: () => api.get<{ meals: AdminMeal[] }>('/api/speiseplan/meals/all'),
  })
}

function useInvalidateMeals() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['speiseplan-meals-admin'] })
    qc.invalidateQueries({ queryKey: ['speiseplan-meals'] })
  }
}
export function useCreateMeal() {
  const invalidate = useInvalidateMeals()
  return useMutation({
    mutationFn: (body: MealPatch) => api.post<{ mealId: number }>('/api/speiseplan/meals', body),
    onSuccess: invalidate,
  })
}
export function useUpdateMeal() {
  const invalidate = useInvalidateMeals()
  return useMutation({
    mutationFn: ({ mealId, ...body }: MealPatch & { mealId: number }) =>
      api.put<{ updated: boolean }>(`/api/speiseplan/meals/${mealId}`, body),
    onSuccess: invalidate,
  })
}
export interface BulkPatch { category?: string; isActive?: boolean; isBio?: boolean; isHauptgericht?: boolean; isSalat?: boolean; isDessert?: boolean; allergens?: string; additives?: string }
export function useBulkMeals() {
  const invalidate = useInvalidateMeals()
  return useMutation({
    mutationFn: ({ mealIds, patch, confirm }: { mealIds: number[]; patch?: BulkPatch; confirm?: boolean }) =>
      api.post<{ updated: number }>('/api/speiseplan/meals/bulk', { mealIds, patch, confirm }),
    onSuccess: invalidate,
  })
}
export function useDeleteMeal() {
  const invalidate = useInvalidateMeals()
  return useMutation({
    mutationFn: (mealId: number) => api.delete<{ deleted: boolean }>(`/api/speiseplan/meals/${mealId}`),
    onSuccess: invalidate,
  })
}

// Change history (temporal table, mig 224)
export interface MealChange { at: string; by: string | null; fields: { field: string; from: string; to: string }[] }
export function useMealHistory(mealId: number | null) {
  return useQuery({
    queryKey: ['speiseplan-meal-history', mealId],
    queryFn: () => api.get<{ created: { at: string; by: string | null } | null; changes: MealChange[] }>(`/api/speiseplan/meals/${mealId}/history`),
    enabled: mealId != null,
  })
}

// Meal feedback (mig 228) — stars per (meal, school, person)
export interface FeedbackEntry { school: string; rating: number; comment: string; by: string; at: string; mine: boolean }
export interface MealFeedback {
  avg: number | null; count: number
  distribution: { rating: number; n: number }[]
  schools: { school: string; avg: number; count: number }[]
  entries: FeedbackEntry[]
  entriesCapped: boolean
}
export function useMealFeedback(mealId: number | null) {
  return useQuery({
    queryKey: ['speiseplan-meal-feedback', mealId],
    queryFn: () => api.get<MealFeedback>(`/api/speiseplan/meals/${mealId}/feedback`),
    enabled: mealId != null,
  })
}
export function useSaveFeedback() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mealId, ...body }: { mealId: number; school: string; rating: number; comment?: string }) =>
      api.post<{ saved: boolean }>(`/api/speiseplan/meals/${mealId}/feedback`, body),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['speiseplan-meal-feedback', v.mealId] })
      qc.invalidateQueries({ queryKey: ['speiseplan-meals-admin'] })
    },
  })
}

// Planned usage — aggregated (a dish may have 250+ uses; groups/months scale)
export interface PlannedUse { date: string; lane: string; group: string }
export interface PlannedSummary {
  total: number; firstDate: string | null; lastDate: string | null
  byGroup: { group: string; n: number }[]
  byMonth: { month: string; n: number }[]
  planned: PlannedUse[]
}
export function useMealPlanned(mealId: number | null, all = false) {
  return useQuery({
    queryKey: ['speiseplan-meal-planned', mealId, all],
    queryFn: () => api.get<PlannedSummary>(`/api/speiseplan/meals/${mealId}/planned${all ? '?all=1' : ''}`),
    enabled: mealId != null,
  })
}

// Meal ↔ supplier products (mig 225)
export interface MealProduct { linkId: number; articleId: number; name: string; artikelNr: string; isOrganic: boolean; supplier: string; note: string }
export interface PricePoint { date: string; price: number }
export interface Article {
  articleId: number; name: string; artikelNr: string; isOrganic: boolean; supplier: string
  unit: { catalogUnit: string | null; packQty: number | null; baseUnit: string | null } | null
  price: { list: number; perBase: number | null; validFrom: string } | null
  /** Announced price not yet valid — suppliers ship catalogs in advance. */
  future: { perBase: number; validFrom: string } | null
  history: PricePoint[]
  trendPct: number | null
}
export function useMealProducts(mealId: number | null) {
  return useQuery({
    queryKey: ['speiseplan-meal-products', mealId],
    queryFn: () => api.get<{ products: MealProduct[] }>(`/api/speiseplan/meals/${mealId}/products`),
    enabled: mealId != null,
  })
}
export function useAddMealProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mealId, articleId, note }: { mealId: number; articleId: number; note?: string }) =>
      api.post<{ linkId: number | null }>(`/api/speiseplan/meals/${mealId}/products`, { articleId, note }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['speiseplan-meal-products', v.mealId] })
      qc.invalidateQueries({ queryKey: ['speiseplan-meals-admin'] })
    },
  })
}
export function useRemoveMealProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mealId, linkId }: { mealId: number; linkId: number }) =>
      api.delete<{ deleted: boolean }>(`/api/speiseplan/meals/${mealId}/products/${linkId}`),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['speiseplan-meal-products', v.mealId] })
      qc.invalidateQueries({ queryKey: ['speiseplan-meals-admin'] })
    },
  })
}
export function useArticleSearch(q: string, supplier: string) {
  return useQuery({
    queryKey: ['speiseplan-articles', q, supplier],
    queryFn: () => api.get<{ articles: Article[] }>(
      `/api/speiseplan/articles?q=${encodeURIComponent(q)}${supplier ? `&supplier=${encodeURIComponent(supplier)}` : ''}`),
    enabled: q.trim().length >= 2,
    staleTime: 60 * 1000,
  })
}

export interface ArticleSupplier { id: string; name: string; articles: number }
export function useArticleSuppliers() {
  return useQuery({
    queryKey: ['speiseplan-article-suppliers'],
    queryFn: () => api.get<{ suppliers: ArticleSupplier[] }>('/api/speiseplan/article-suppliers'),
    staleTime: 10 * 60 * 1000,
  })
}

// ── Per-meal history stats (feeds the week generator's scoring) ──────────────
export interface MealStat { mealId: number; timesPlanned: number; lastServed: string | null }

export function useMealStats(group: string | null) {
  return useQuery({
    queryKey: ['speiseplan-meal-stats', group],
    queryFn: () => api.get<{ stats: MealStat[] }>(`/api/speiseplan/meal-stats?group=${encodeURIComponent(group!)}`),
    enabled: !!group,
    staleTime: 5 * 60 * 1000,
  })
}

// ── Historical plan import (MensaMax export CSV — see mensamax-export-speiseplan-historie.sql) ─
export interface PlanImportResult {
  plansImported: number; slotsImported: number
  skipped: { row: number; reason: string }[]
  unresolvedMealIds: string[]; note?: string
}

export function useImportPlans() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (csv: string) => api.post<PlanImportResult>('/api/speiseplan/plans/import', { csv }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-plan'] }),
  })
}

// ── SMTP settings (IT.Admin only — set from the UI instead of Function App settings) ─
export interface SmtpSettings {
  id: number; smtpHost: string | null; smtpPort: number; smtpUser: string | null; smtpFrom: string
  hasPassword: boolean; active: boolean
  lastTestAt: string | null; lastTestOk: boolean | null; lastTestMsg: string | null
}
export interface SmtpSettingsPatch { smtpHost: string; smtpPort: number; smtpUser: string; smtpFrom: string; active: boolean; password?: string }

export function useSmtpSettings() {
  return useQuery({
    queryKey: ['speiseplan-smtp'],
    queryFn: () => api.get<{ settings: SmtpSettings }>('/api/speiseplan/smtp'),
  })
}

export function useSaveSmtpSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: SmtpSettingsPatch) => api.put<{ settings: SmtpSettings }>('/api/speiseplan/smtp', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-smtp'] }),
  })
}

export function useTestSmtp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; message?: string }>('/api/speiseplan/smtp/test', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-smtp'] }),
  })
}

// ── Mailing list ──────────────────────────────────────────────────────────────
export interface MailContact { contactId: number; schoolId: number; address: string; kind: Channel; label: string | null }
export interface MailSchool extends GroupSchool { contacts: MailContact[] }

export function useMailContacts(group: string | null) {
  return useQuery({
    queryKey: ['speiseplan-mailcontacts', group],
    queryFn: () => api.get<{ schools: MailSchool[] }>(`/api/speiseplan/mail-contacts?group=${encodeURIComponent(group!)}`),
    enabled: !!group,
  })
}

export function useAddContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { schoolId: number; address: string; kind: Channel; label?: string }) =>
      api.post<{ saved: boolean }>('/api/speiseplan/mail-contacts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-mailcontacts'] }),
  })
}

// ── Catalog import (MensaMax export CSV — see mensamax-export-speisekarte.sql) ─
export interface ImportResult { imported: number; inserted: number; updated: number; skipped: { row: number; reason: string }[] }

export function useImportMeals() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (csv: string) => api.post<ImportResult>('/api/speiseplan/meals/import', { csv }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-meals'] }),
  })
}

export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: number) => api.delete<{ deleted: boolean }>(`/api/speiseplan/mail-contacts?id=${contactId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speiseplan-mailcontacts'] }),
  })
}
